import React, { useEffect, useState } from 'react';
import {
  Animated,
  Easing,
  StyleProp,
  TextStyle,
  ViewProps,
  ViewStyle,
} from 'react-native';

import { Feedback } from './feedback/feedback';
import { useKeyPress } from '../..//hooks/use-key-press';
import { useColors } from '../../hooks/lesson-player.context';
import ActivityFooter from '../activity-footer/activity-footer';
import { Button } from '../button/button';

interface ContinueButtonProps extends ViewProps {
  onPress: () => void;
  isCorrect?: boolean;
  blocked?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  action?: string;
  disabled?: boolean;
  feedbackTitle?: string;
  feedback?: string;
  hasSafeArea?: boolean;
  successColor?: string;
  errorColor?: string;
  animated?: boolean;
  animationDuration?: number;
}

export function ContinueButton({
  onPress,
  isCorrect = true,
  containerStyle,
  action = 'Next',
  feedbackTitle,
  feedback,
  hasSafeArea = true,
  successColor,
  errorColor,
  textStyle,
  animated = true,
  animationDuration = 350,
}: ContinueButtonProps) {
  const colors = useColors();
  const [visibility] = useState(new Animated.Value(animated ? 0 : 1));
  const [height, setHeight] = useState(0);
  const enterPress = useKeyPress('Enter');
  const successButtonColor = successColor || colors.success;
  const errorButtonColor = errorColor || colors.error;

  useEffect(() => {
    Animated.timing(visibility, {
      toValue: 1,
      duration: animationDuration,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [visibility, animationDuration]);

  useEffect(() => {
    if (enterPress) {
      onPress();
    }
  }, [enterPress, onPress]);

  function onLayout({
    nativeEvent: {
      layout: { height },
    },
  }) {
    setHeight(height);
  }

  const translateY = visibility.interpolate({
    inputRange: [0, 1],
    outputRange: [height, 0],
  });

  const opacity = visibility.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0, 0, 1],
  });

  return (
    <Animated.View
      onLayout={onLayout}
      style={[{ transform: [{ translateY }], opacity }]}
    >
      <ActivityFooter style={containerStyle} safeArea={hasSafeArea}>
        {isCorrect && <Feedback title={feedbackTitle} message={feedback} />}
        <ActivityFooter.ButtonContainer>
          <Button
            textStyle={textStyle}
            backgroundColor={isCorrect ? successButtonColor : errorButtonColor}
            onPress={onPress}
          >
            {action}
          </Button>
        </ActivityFooter.ButtonContainer>
      </ActivityFooter>
    </Animated.View>
  );
}
